import { useCallback, useState } from 'react';
import { useFetchConfigurationItem } from '../../dependencies/useFetchConfigurationItem';
import { ConfigurationType } from '../../../../../documentation/dependencies/types';
import { translateNewConfigurationTypeKeysToOldVersion } from '../../../../../documentation/dependencies/helpers';
import groupBy from 'lodash/groupBy';
import { isMetadataElementInCanvas } from '../helper';
import {
  ConfigurationDependencies,
  ConfigurationDependenciesWithParentType,
} from '../../../../../documentation/dependencies/DependenciesTypes';
import { FlowProperties, BaseElementProperties, BaseFieldProperties } from '@server/documentation';
import { useRunOnce } from '../../../../../common/useRunOnce';
import {
  MetadataElementWithoutConnections,
  useAddMetadataElementToCanvas,
} from '../../../metadata-elements/useAddMetadataElementToCanvas';
import { telemetry } from '../../../../../../telemetry';
import { RelationshipsMenuView } from './RelationshipsMenuView';
import { MetadataRelationshipWithParentType } from './types';
import { useDocumentationApiFacade } from '../../../../../../apis/facades/useDocumentationApiFacade';

interface MetadataDependenciesMenuProps {
  onClose: () => void;
  anchorEl: (EventTarget & HTMLLIElement) | null;
  funnelMap: FunnelMap;
  fullMetadataId: string;
  closeAll: () => void;
}
type Dependencies = Partial<ConfigurationDependencies> & {
  [key: string]: (BaseElementProperties | BaseFieldProperties | FlowProperties)[];
};

type DependenciesWithParentType = Partial<ConfigurationDependenciesWithParentType> & {
  [key: string]: MetadataRelationshipWithParentType[];
};

const stateMessages = {
  loading: 'Loading dependencies...',
  noRelationships: 'Element has no dependencies',
  allRelationshipsAdded: 'All dependencies added to the canvas',
  error: 'Error fetching dependencies. Please try again.',
  hasRelationships: 'Add dependencies to the canvas',
};

export const MetadataDependenciesMenu = ({
  onClose,
  anchorEl,
  funnelMap,
  fullMetadataId,
  closeAll,
}: MetadataDependenciesMenuProps) => {
  const [dependencies, setDependencies] = useState<DependenciesWithParentType>({});
  const { crmOrgId, metadataId: originMetadataId } = funnelMap.metadataElements[fullMetadataId];
  const [state, setState] = useState<
    'loading' | 'error' | 'hasRelationships' | 'noRelationships' | 'allRelationshipsAdded'
  >('loading');

  const { get_crmOrgConfigurationDependenciesFromSalto } = useDocumentationApiFacade();
  const { fetchConfigurationItem } = useFetchConfigurationItem();

  useRunOnce(async () => {
    try {
      const item = await fetchConfigurationItem(originMetadataId, crmOrgId);

      const dependencies = await get_crmOrgConfigurationDependenciesFromSalto({
        crmOrgId,
        configurationName: item.name,
        filename: item.filename,
        objectName: '',
        configurationKey: '',
      });

      const { [ConfigurationType.others]: others, ...dependenciesWithoutOthers } = dependencies;

      const itemsWithTranslatedKeys = others.map((other) => ({
        ...other,
        type: translateNewConfigurationTypeKeysToOldVersion(other.type as string),
      }));
      const groupedItems = groupBy(itemsWithTranslatedKeys, 'type');
      const _dependencies: Dependencies = { ...dependenciesWithoutOthers };

      // merge grouped items with dependencies without others
      Object.keys(groupedItems).forEach((key) => {
        if (key in _dependencies) {
          _dependencies[key] = [..._dependencies[key], ...groupedItems[key]];
        } else {
          _dependencies[key] = groupedItems[key];
        }
      });

      // Filter all items to only include those that are not already in canvas
      const filteredDependencies: DependenciesWithParentType = Object.fromEntries(
        Object.entries(_dependencies)
          .map(([key, value]) => [
            key,
            value
              .filter(
                (item) =>
                  !isMetadataElementInCanvas({
                    metadataId: item.metadataId,
                    crmOrgId,
                    canvasFunnelMap: funnelMap,
                  }),
              )
              .map((item) => ({ ...item, parentType: key })),
          ])
          .filter(([, value]) => value.length > 0),
      );

      setDependencies(filteredDependencies);
      const _dependenciesCount = Object.values(_dependencies).flat().length;
      const filteredDependenciesCount = Object.values(filteredDependencies).flat().length;

      if (filteredDependenciesCount > 0) {
        setState('hasRelationships');
      } else {
        if (_dependenciesCount > 0) {
          setState('allRelationshipsAdded');
        } else {
          setState('noRelationships');
        }
      }
    } catch (error) {
      telemetry.captureError(error);
      console.error('Error fetching dependencies:', error);
      setState('error');
    }
  });

  const { addMetadataElementsToCanvasAndEnrichConnections } = useAddMetadataElementToCanvas();

  const addDependenciesToCanvas = useCallback(
    (dependencies: MetadataRelationshipWithParentType[]) => {
      const metadataElements: MetadataElementWithoutConnections[] = dependencies.map(
        (dependency, index) => ({
          name: dependency.label,
          type: dependency.type,
          crmOrgId,
          metadataId: dependency.metadataId,
          parentType: dependency.parentType,
          position: {
            column: 0,
            row: -index,
          },
        }),
      );
      addMetadataElementsToCanvasAndEnrichConnections({
        metadataElements,
        canvasFunnelMap: funnelMap,
        parentElement: {
          metadataId: originMetadataId,
          crmOrgId,
        },
      });
      closeAll();
    },
    [
      addMetadataElementsToCanvasAndEnrichConnections,
      funnelMap,
      originMetadataId,
      crmOrgId,
      closeAll,
    ],
  );

  const entries = Object.entries(dependencies).map(([key, value]) => ({
    key,
    data: value as MetadataRelationshipWithParentType[],
    count: value.length,
  }));

  return (
    <RelationshipsMenuView
      state={state}
      entries={entries}
      onEntryClick={addDependenciesToCanvas}
      onClose={onClose}
      anchorEl={anchorEl}
      stateMessages={stateMessages}
    />
  );
};
